// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { categoryList } from "@mcmc/Overrides/Widgets/Category/CategoryList.style";
import StyledBox from "@mcmc/XDL/Components/Common/Box";
import CloudinaryImage from "@mcmc/XDL/Components/Common/CloudinaryImage";
import { transformationsMap } from "@mcmc/XDL/Utilities/cloudinary";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    categoryDetailSubCategoriesWrapper?: InjectableCss;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    categoryDetailSubCategoriesWrapper: {
        css: css`
            padding-top: 30px;
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
};

const CategoryDetailSubCategories = ({ category, fields: { showImages } }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    const [styles] = React.useState(() => mergeToNew(categoryDetailSubCategoriesStyles, categoryList));

    return (
        <StyledWrapper {...styles.categoryDetailSubCategoriesWrapper}>
            <StyledWrapper {...styles.categoriesContentWrapper}>
                {category.subCategoryIds.map(subCategoryId => (
                    <SubCategoryLink
                        categoryId={subCategoryId}
                        showImages={showImages}
                        key={subCategoryId}
                        styles={styles}
                    />
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
    key,
    styles,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean } & { key: string } & { styles: any }) => {
    if (!category) {
        return null;
    }

    return (
        <StyledBox {...styles.styledBox} key={key}>
            <Link
                href={category.path}
                {...styles.link}
                data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
            >
                {showImages && (
                    <StyledWrapper {...styles.categoryImageWrapper}>
                        <CloudinaryImage
                            imageTransformations={transformationsMap.product_sm}
                            src={category.smallImagePath}
                            altText={category.imageAltText}
                        />
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.categoryNameWrapper}>
                    <Typography {...styles.categoryName}>{category.shortDescription}</Typography>
                </StyledWrapper>
            </Link>
        </StyledBox>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailSubCategories),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
